import { Fragment, useState } from 'react'
import type { CSSProperties } from 'react'
import type { GccCapabilitySourcing, GccLocation, GccOwnership, GccSourcingAllocation, GccStage2Result, GccTaskSourcing } from '@shared/types'
import { GCC_AUTOMATE_FIRST_LABEL, GCC_LOCATIONS, GCC_OWNERSHIPS, formatSourcingPair } from '@tn/shared'
import { GccTooltip, TipEnum } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { LOCATION_META, OWNERSHIP_META, TIER_META } from './gcc-meta'
import { GCC_BADGE } from './gccExecutiveStyle'
import type { GccBadgeStyle } from './gccExecutiveStyle'

const NEUTRAL = GCC_BADGE.gray

/** Tier badges, as in the exported GCC dashboard (WI 2487). */
export const TIER_STYLE: Record<string, GccBadgeStyle> = {
  HQ:              GCC_BADGE.blue,
  Markets:         GCC_BADGE.purple,
  'GCC-Offshore':  GCC_BADGE.green,
  'GCC-Nearshore': GCC_BADGE.yellow,
  MSP:             GCC_BADGE.orange,
}

/** Ownership badges as in the export; bars use the categorical colours of GCC_EXECUTIVE_TOKENS. */
export const OWNERSHIP_STYLE: Record<GccOwnership, GccBadgeStyle & { bar: string }> = {
  'Captive-GCC': { ...GCC_BADGE.cyan, bar: 'var(--c-gcc)' },
  MSP:           { ...GCC_BADGE.orange, bar: 'var(--c-msp)' },
  Retained:      { ...GCC_BADGE.blue, bar: 'var(--c-ret)' },
}

/** FTE with at most two decimals, without trailing zeros: allocations are exact to 0.01 FTE. */
export function fmtFte2(fte: number): string {
  return String(Math.round(fte * 100) / 100)
}

/** True when a Step 2 result has the v2 ownership × location allocation (WI 2324). */
export function isV2Sourcing(stage2: GccStage2Result | undefined): boolean {
  return !!stage2?.sourcingModel?.some(c => Array.isArray(c.allocation))
}

/** True when any part of the capability's allocation matches the filters. */
export function matchesSourcingFilter(
  cap: GccCapabilitySourcing,
  ownership: GccOwnership | null,
  location: GccLocation | null,
  hybridOnly: boolean,
): boolean {
  if (hybridOnly && !cap.isHybrid) return false
  if (!ownership && !location) return true
  return (cap.allocation ?? []).some(a => (!ownership || a.ownership === ownership) && (!location || a.location === location))
}

/**
 * A toggleable filter pill in the export's style, shared by the dashboard tabs. A
 * category pill carries a swatch in its badge colour.
 */
export function Chip({ label, active, onClick, style }: { label: string; active: boolean; onClick: () => void; style?: GccBadgeStyle }) {
  return (
    <button onClick={onClick} aria-pressed={active} className={`gx-pill${active ? ' is-active' : ''}`}>
      {style && <i aria-hidden="true" style={{ '--sw': style.text } as CSSProperties} />}
      {label}
    </button>
  )
}

/** A tinted badge in the export's style. */
export function Badge({ label, style, className = '' }: { label: string; style?: GccBadgeStyle; className?: string }) {
  const s = style ?? NEUTRAL
  return <span className={`gx-badge ${className}`.trim()} style={{ background: s.bg, color: s.text }}>{label}</span>
}

/** v1 designs: one tier per capability, filterable by tier and signal. */
function LegacySourcing({ rows }: { rows: GccCapabilitySourcing[] }) {
  const [tierFilter, setTierFilter] = useState<string | null>(null)
  const [signalFilter, setSignalFilter] = useState<string | null>(null)
  const tiers = [...new Set(rows.map(r => r.sourcingTier))]
  const signals = [...new Set(rows.map(r => r.signalSource).filter(Boolean))]
  const filtered = rows.filter(r => (!tierFilter || r.sourcingTier === tierFilter) && (!signalFilter || r.signalSource === signalFilter))
  const hasFilter = !!tierFilter || !!signalFilter

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4 pb-3 border-b border-gray-100 text-xs">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Tier</span>
        {tiers.map(t => (
          <Chip key={t} label={t} active={tierFilter === t} style={TIER_STYLE[t]} onClick={() => setTierFilter(p => p === t ? null : t)} />
        ))}
        {signals.length > 0 && <span className="text-gray-200 mx-0.5">|</span>}
        {signals.length > 0 && <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Signal</span>}
        {signals.map(s => (
          <Chip key={s} label={s} active={signalFilter === s} style={s === 'TN' ? GCC_BADGE.blue : GCC_BADGE.gray} onClick={() => setSignalFilter(p => p === s ? null : s)} />
        ))}
        {hasFilter && (
          <button onClick={() => { setTierFilter(null); setSignalFilter(null) }} className="gx-link ml-1">Clear</button>
        )}
        <span className="ml-auto text-gray-400 text-[11px]">
          {hasFilter ? `${filtered.length} of ${rows.length}` : `${rows.length} rows`}
        </span>
      </div>
      <div className="gx-tbl-wrap">
        <table className="gx-tbl w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
              <th className="pb-3 font-medium w-48"><GccTooltip content={GCC_TOOLTIPS.sourcingCapability}>Capability</GccTooltip></th>
              <th className="pb-3 font-medium w-40">
                <GccTooltip content={<>{GCC_TOOLTIPS.sourcingTier}<TipEnum entries={TIER_META} /></>}>Sourcing Tier</GccTooltip>
              </th>
              <th className="pb-3 font-medium w-16"><GccTooltip content={GCC_TOOLTIPS.sourcingSignal}>Signal</GccTooltip></th>
              <th className="pb-3 font-medium"><GccTooltip content={GCC_TOOLTIPS.sourcingRationale}>Rationale</GccTooltip></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((cap, i) => {
              const ts = TIER_STYLE[cap.sourcingTier] ?? NEUTRAL
              return (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 font-semibold text-gray-800 align-top">{cap.capabilityName}</td>
                  <td className="py-3 align-top"><Badge label={cap.sourcingTier} style={ts} /></td>
                  <td className="py-3 text-xs text-gray-400 align-top">{cap.signalSource}</td>
                  <td className="py-3 text-xs text-gray-500 leading-relaxed">{cap.rationale}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Colour of one ownership × location in the split bar: the ownership's colour, faded
 * for Nearshore and Markets. The chip swatches use it too, so each chip matches its
 * segment (WI 2571).
 */
export function segmentStyle(a: Pick<GccSourcingAllocation, 'ownership' | 'location'>): { background: string; opacity: number } {
  return { background: OWNERSHIP_STYLE[a.ownership].bar, opacity: a.location === 'Offshore' || a.location === 'HQ' ? 1 : 0.6 }
}

/** Stacked bar of a capability's allocation, one segment per ownership × location. */
function SplitBar({ cap }: { cap: GccCapabilitySourcing }) {
  const allocation = cap.allocation ?? []
  if (allocation.length === 0) return null
  return (
    <div className="gx-bar flex h-2 w-full rounded-full overflow-hidden bg-gray-100" role="img"
      aria-label={allocation.map(a => `${formatSourcingPair(a.ownership, a.location)} ${a.sharePct}%`).join(', ')}>
      {allocation.map((a, i) => (
        <div key={i} data-testid="sourcing-segment" style={{ width: `${a.sharePct}%`, ...segmentStyle(a) }} />
      ))}
    </div>
  )
}

/** v2 designs (WI 2324): residual FTE per capability, split across ownership × location. */
function ResidualSourcing({ stage2 }: { stage2: GccStage2Result }) {
  const [ownershipFilter, setOwnershipFilter] = useState<GccOwnership | null>(null)
  const [locationFilter, setLocationFilter] = useState<GccLocation | null>(null)
  const [hybridOnly, setHybridOnly] = useState(false)
  const [openRow, setOpenRow] = useState<number | null>(null)

  const rows = stage2.sourcingModel
  const tasksByCapability = new Map<string, GccTaskSourcing[]>()
  for (const t of stage2.taskSourcing ?? []) {
    const key = `${t.functionScope}/${t.capabilityName}`
    tasksByCapability.set(key, [...(tasksByCapability.get(key) ?? []), t])
  }
  const allParts = rows.flatMap(r => r.allocation ?? [])
  const total = rows.reduce((s, r) => s + (r.residualFte ?? 0), 0)
  const byOwnership = GCC_OWNERSHIPS.map(o => ({ o, fte: allParts.filter(a => a.ownership === o).reduce((s, a) => s + a.fte, 0) }))
  const hybridCount = rows.filter(r => r.isHybrid).length
  const filtered = rows.filter(r => matchesSourcingFilter(r, ownershipFilter, locationFilter, hybridOnly))
  const hasFilter = !!ownershipFilter || !!locationFilter || hybridOnly
  const defaulted = stage2.defaultedTaskIds ?? []
  const automateFirst = stage2.automateFirstTaskIds ?? []
  const automateFirstCountByCapability = new Map(
    [...tasksByCapability.entries()].map(([key, tasks]) => [key, tasks.filter(t => t.automateFirst).length])
  )

  return (
    <div>
      <section aria-label="Sourcing summary" className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-gray-500">
        <span><span className="font-semibold text-gray-800">{fmtFte2(Math.round(total * 100) / 100)}</span> residual FTE sourced</span>
        {byOwnership.map(({ o, fte }) => (
          <span key={o}>
            <span className="font-medium" style={{ color: OWNERSHIP_STYLE[o].text }}>{o === 'Captive-GCC' ? 'Captive GCC' : o}</span> {fmtFte2(fte)}
          </span>
        ))}
        <span>{hybridCount} of {rows.length} capabilit{rows.length === 1 ? 'y' : 'ies'} hybrid</span>
        {stage2.automateFirstThresholdPct !== undefined && (
          <GccTooltip content={GCC_TOOLTIPS.sourcingAutomateFirst}>
            <span data-testid="automate-first-summary">
              <span className="font-semibold" style={{ color: GCC_BADGE.cyan.text }}>{automateFirst.length}</span> task{automateFirst.length !== 1 ? 's' : ''} automate first (AI releases {stage2.automateFirstThresholdPct}% or more), kept Retained
            </span>
          </GccTooltip>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-1.5 mb-4 pb-3 border-b border-gray-100 text-xs">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">
          <GccTooltip content={<>{GCC_TOOLTIPS.sourcingOwnership}<TipEnum entries={OWNERSHIP_META} /></>}>Ownership</GccTooltip>
        </span>
        {GCC_OWNERSHIPS.map(o => (
          <Chip key={o} label={o === 'Captive-GCC' ? 'Captive GCC' : o} active={ownershipFilter === o} style={OWNERSHIP_STYLE[o]}
            onClick={() => setOwnershipFilter(p => p === o ? null : o)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">
          <GccTooltip content={<>{GCC_TOOLTIPS.sourcingLocation}<TipEnum entries={LOCATION_META} /></>}>Location</GccTooltip>
        </span>
        {GCC_LOCATIONS.map(l => (
          <Chip key={l} label={l} active={locationFilter === l} onClick={() => setLocationFilter(p => p === l ? null : l)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <Chip label="Hybrid only" active={hybridOnly} style={GCC_BADGE.yellow} onClick={() => setHybridOnly(p => !p)} />
        {hasFilter && (
          <button onClick={() => { setOwnershipFilter(null); setLocationFilter(null); setHybridOnly(false) }} className="gx-link ml-1">Clear</button>
        )}
        <span className="ml-auto text-gray-400 text-[11px]">
          {hasFilter ? `${filtered.length} of ${rows.length}` : `${rows.length} rows`}
        </span>
      </div>

      <div className="gx-tbl-wrap">
        <table className="gx-tbl w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
              <th className="pb-3 font-medium w-48"><GccTooltip content={GCC_TOOLTIPS.sourcingCapability}>Capability</GccTooltip></th>
              <th className="pb-3 font-medium w-20 text-right"><GccTooltip content={GCC_TOOLTIPS.sourcingResidualFte}>Residual FTE</GccTooltip></th>
              <th className="pb-3 font-medium w-72 pl-4 pr-8"><GccTooltip content={GCC_TOOLTIPS.sourcingSplit}>Sourcing split</GccTooltip></th>
              <th className="pb-3 font-medium"><GccTooltip content={GCC_TOOLTIPS.sourcingRationale}>Rationale</GccTooltip></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((cap, i) => {
              const tasks = tasksByCapability.get(`${cap.functionScope}/${cap.capabilityName}`) ?? []
              const isOpen = openRow === i
              return (
                <Fragment key={i}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 align-top">
                      <div className="font-semibold text-gray-800">{cap.capabilityName}</div>
                      <div className="text-[10px] text-gray-400">{cap.functionScope}</div>
                      {cap.isHybrid && (
                        <Badge label="Hybrid" style={GCC_BADGE.yellow} className="mt-1" />
                      )}
                      {(automateFirstCountByCapability.get(`${cap.functionScope}/${cap.capabilityName}`) ?? 0) > 0 && (
                        <span className="gx-badge mt-1 ml-1" style={{ background: GCC_BADGE.cyan.bg, color: GCC_BADGE.cyan.text }}>
                          {automateFirstCountByCapability.get(`${cap.functionScope}/${cap.capabilityName}`)} automate first
                        </span>
                      )}
                    </td>
                    <td className="py-3 align-top text-right font-semibold text-gray-900">{fmtFte2(cap.residualFte ?? 0)}</td>
                    <td className="py-3 align-top pl-4 pr-8">
                      {(cap.allocation ?? []).length === 0 ? (
                        <span className="text-xs text-gray-400">No residual work</span>
                      ) : (
                        <>
                          <SplitBar cap={cap} />
                          <ul className="mt-1.5 space-y-0.5">
                            {(cap.allocation ?? []).map((a, j) => (
                              <li key={j} className="flex items-center gap-1.5 text-xs">
                                <span className="gx-badge" style={{ background: OWNERSHIP_STYLE[a.ownership].bg, color: OWNERSHIP_STYLE[a.ownership].text }}>
                                  <i aria-hidden="true" data-testid="sourcing-swatch" className="gx-sw" style={segmentStyle(a)} />
                                  {formatSourcingPair(a.ownership, a.location)}
                                </span>
                                <span className="font-semibold text-gray-700">{fmtFte2(a.fte)} FTE</span>
                                <span className="text-gray-400">({a.sharePct}%)</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </td>
                    <td className="py-3 text-xs text-gray-500 leading-relaxed align-top">
                      {cap.rationale}
                      {tasks.length > 0 && (
                        <button onClick={() => setOpenRow(isOpen ? null : i)} aria-expanded={isOpen}
                          className="gx-link block mt-1">
                          {isOpen ? 'Hide' : 'Show'} {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={4} className="pb-3">
                        <table className="gx-tbl w-full text-xs bg-gray-50 rounded">
                          <thead>
                            <tr className="text-left text-gray-400">
                              <th className="px-3 py-1.5 font-medium">Task</th>
                              <th className="px-3 py-1.5 font-medium text-right">Residual FTE</th>
                              <th className="px-3 py-1.5 font-medium">Ownership · location</th>
                              <th className="px-3 py-1.5 font-medium">Rationale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tasks.map(t => (
                              <tr key={t.taskId}>
                                <td className="px-3 py-1 text-gray-700">
                                  {t.taskName}
                                  {t.automateFirst && (
                                    <Badge label={GCC_AUTOMATE_FIRST_LABEL} style={GCC_BADGE.cyan} className="ml-1.5" />
                                  )}
                                </td>
                                <td className="px-3 py-1 text-right text-gray-700">{fmtFte2(t.residualFte)}</td>
                                <td className="px-3 py-1 text-gray-700" title={t.defaulted ? 'Not sourced validly by Step 2 — kept at Retained · HQ' : undefined}>
                                  {formatSourcingPair(t.ownership, t.location)}
                                  {t.defaulted && <span className="ml-1 text-amber-600">*</span>}
                                </td>
                                <td className="px-3 py-1 text-gray-500">{t.rationale}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {defaulted.length > 0 && (
        <p className="mt-2 text-[10px] text-amber-700">
          {defaulted.length} task{defaulted.length !== 1 ? 's were' : ' was'} not sourced validly by Step 2 and kept at Retained · HQ.
        </p>
      )}
    </div>
  )
}

/**
 * Sourcing Model tab. v2 designs (WI 2324) show each capability's residual FTE
 * split across ownership × location; v1 designs keep the one-tier-per-capability view.
 */
export default function GccSourcingModel({ stage2 }: { stage2: GccStage2Result | undefined }) {
  const rows = stage2?.sourcingModel ?? []
  if (!stage2 || rows.length === 0) return <p className="text-sm text-gray-400">No sourcing model data.</p>
  return isV2Sourcing(stage2) ? <ResidualSourcing stage2={stage2} /> : <LegacySourcing rows={rows} />
}
